import React, { useState, useEffect, useRef, useMemo } from "react";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./custom-quill-theme.css";
import katex from "katex";
import "katex/dist/katex.min.css";
import imageResize from "quill-image-resize-module-react";
import { extractBase64AndCreateFile, replaceBase64WithIdentifier } from "../../../../../helpers";
import { $host } from "../../../../../http"; // Импортируем HTTP клиент

Quill.register("modules/imageResize", imageResize);

window.katex = katex;

const uploadImageToServer = async (file) => {
    try {
        const formData = new FormData();
        formData.append("photo", file);

        const response = await $host.post("/api/teachers/questions/upload-image", formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });

        return response.data.img_url;
    } catch (error) {
        console.error("Ошибка загрузки изображения:", error);
        return null;
    }
};

const addImgSrcToContent = (content, imgUrl) => {
    if (!content || !imgUrl) return content;

    return content.replace(/<img\s+([^>]*?)src="[^"]*"([^>]*)>/g, (match, beforeSrc, afterSrc) => {
        return `<img ${beforeSrc.trim()} src="${imgUrl}" ${afterSrc.trim()}>`;
    });
};

const addWidthToImageTag = (html, width) => {
    if (html === null) return null;
    if (typeof width !== "number") {
        return html;
    }
    return html.replace(/<img\s+[^>]*>/g, (match) => {
        if (match.includes('width="')) {
            return match.replace(/width="\d*"/, `width="${width}"`);
        }
        return match.replace('<img', `<img width="${width}"`);
    });
};

const baseModules = {
    toolbar: [
        ["bold", "italic", "underline", "strike"],
        ["blockquote", "code-block"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ script: "sub" }, { script: "super" }],
        [{ header: [1, 2, 3, false] }],
        ["link", "formula"],
        [{ color: [] }, { background: [] }],
        [{ align: [] }],
        ["clean"],
    ],
    clipboard: { matchVisual: false },
};

const baseFormats = [
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "code-block",
    "list",
    "bullet",
    "header",
    "link",
    "formula",
    "color",
    "background",
    "align",
];

const imageModules = {
    imageResize: {
        modules: ["Resize", "DisplaySize"],
    },
};

export const TextEditor = ({ setPayload, data, placeholder, disableImages = false }) => {
    const computedInitialEditorHtml = addImgSrcToContent(data?.title ?? '', data?.photo_url);
    const [editorHtml, setEditorHtml] = useState(computedInitialEditorHtml);
    const [isImageUploaded, setIsImageUploaded] = useState(false);
    const quillRef = useRef(null);

    const modules = useMemo(() => {
        const modulesConfig = { ...baseModules };

        if (!disableImages) {
            // Убираем дубликаты и добавляем кнопку "image" только один раз
            const newToolbar = modulesConfig.toolbar.map((group) =>
                Array.isArray(group) && (group.includes("link") || group.includes("formula"))
                    ? [...new Set([...group, "image"])] // Используем Set, чтобы убрать дубликаты
                    : group
            );

            modulesConfig.toolbar = newToolbar;
            modulesConfig.imageResize = imageModules.imageResize;
        }

        return modulesConfig;
    }, [disableImages]);

    const formats = useMemo(() => (disableImages ? baseFormats : [...baseFormats, "image"]), [disableImages]);

    const handleChange = (value) => {
        setEditorHtml(value);
        setPayload({ content: value, imgFile: null });

        const imgExists = /<img\s+[^>]*src\s*=\s*["'][^"']*["'][^>]*>/g.test(value);
        setIsImageUploaded(imgExists);
    };

    const handleImageUpload = async () => {
        const input = document.createElement("input");
        input.setAttribute("type", "file");
        input.setAttribute("accept", "image/png, image/jpeg, image/jpg");
        input.click();

        input.onchange = async () => {
            const file = input.files[0];
            if (!file) return;

            const imgUrl = await uploadImageToServer(file);
            if (!imgUrl) {
                console.error("Ошибка загрузки изображения");
                return;
            }

            const quill = quillRef.current.getEditor();
            const range = quill.getSelection();
            quill.insertEmbed(range.index, "image", imgUrl);
        };
    };

    useEffect(() => {
        if (quillRef.current) {
            const quillEditor = quillRef.current.getEditor();
            const toolbar = quillEditor.getModule("toolbar");

            if (toolbar) {
                toolbar.addHandler("image", handleImageUpload);
            }
        }
    }, []);

    return (
        <div className="custom-quill-wrapper">
            <ReactQuill
                ref={quillRef}
                className="h-full"
                theme="snow"
                onChange={handleChange}
                value={editorHtml}
                modules={modules}
                formats={formats}
                bounds={".app"}
                placeholder={placeholder}
            />
        </div>
    );
};
